/*
 * cart-recommendations
 * ---------------------------------------------------------------------------
 * Muestra productos COMPLEMENTARIOS en el cart drawer.
 *
 * mini-cart.liquid entrega dos atributos:
 *   data-url          -> .../recommendations/products?section_id=..&product_id=<primer item>&limit=..&intent=complementary
 *   data-fallback-url -> la misma URL pero con intent=related
 *
 * Problema que resuelve
 * ---------------------
 * El markup ancla las recomendaciones SOLO al primer producto del carrito
 * (cart.items.first). Como buena parte del catalogo de accesorios no tiene
 * complementarios configurados en Search & Discovery, basta con que el primer
 * item sea una funda o un cable para que la respuesta venga vacia.
 *
 * Ademas la seccion siempre renderiza el titulo y el <ul>, incluso sin
 * productos, por lo que medir `innerHTML.length` daba siempre > 0 y el
 * fallback nunca llegaba a dispararse: el bloque quedaba con el titulo solo.
 *
 * Que hace ahora
 * --------------
 * 1. Lee los product_id del carrito desde /cart.js.
 * 2. Prueba intent=complementary con cada producto del carrito, en orden,
 *    hasta que uno devuelva productos de verdad.
 * 3. Si ninguno tiene complementarios, recien ahi usa data-fallback-url
 *    (intent=related).
 * 4. Si tampoco hay relacionados, vacia el bloque para no dejar un titulo
 *    huerfano.
 *
 * Las llamadas son secuenciales a proposito: en el peor caso son pocas y el
 * bloque carga de forma diferida (IntersectionObserver), asi que no compiten
 * con el render inicial del drawer.
 */

// Techo de productos del carrito a consultar, para acotar los requests.
const CART_RECOMMENDATIONS_MAX_CANDIDATES = 5;

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.load();
  }

  async load() {
    try {
      const urls = await this.buildCandidateUrls();

      for (const url of urls) {
        const html = await this.fetchRecommendations(url);
        if (html !== null) {
          this.innerHTML = html;
          this.scrollMiniCartToTop();
          return;
        }
      }

      // Ni complementarios ni relacionados: evitamos dejar el titulo solo.
      this.innerHTML = '';
    } catch (error) {
      console.error('[cart-recommendations]', error);
    }
  }

  /** Arma la lista ordenada de URLs a intentar. */
  async buildCandidateUrls() {
    const base = this.dataset.url;
    if (!base) return [];

    const urls = [];
    const seen = new Set();

    for (const productId of await this.cartProductIds()) {
      if (seen.has(productId)) continue;
      seen.add(productId);

      const url = new URL(base, window.location.origin);
      url.searchParams.set('product_id', productId);
      urls.push(url.toString());

      if (urls.length >= CART_RECOMMENDATIONS_MAX_CANDIDATES) break;
    }

    // Si no se pudo leer el carrito, al menos respetamos la URL original.
    if (urls.length === 0) urls.push(base);

    // Ultimo recurso: intent=related.
    if (this.dataset.fallbackUrl) urls.push(this.dataset.fallbackUrl);

    return urls;
  }

  /** product_id de cada linea del carrito, en orden. */
  async cartProductIds() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) return [];

      const cart = await response.json();
      return (cart.items || []).map((item) => String(item.product_id));
    } catch (error) {
      console.error('[cart-recommendations] no se pudo leer el carrito', error);
      return [];
    }
  }

  /**
   * Devuelve el innerHTML del bloque si trae productos, o null si vino vacio.
   * Contamos <li> reales en vez de medir innerHTML, porque la seccion siempre
   * renderiza el titulo y la <ul> aunque no haya recomendaciones.
   */
  async fetchRecommendations(url) {
    const response = await fetch(url);
    if (!response.ok) return null;

    const text = await response.text();
    const node = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('cart-recommendations');

    if (!node || node.querySelectorAll('li').length === 0) return null;

    return node.innerHTML;
  }

  scrollMiniCartToTop() {
    const miniCartMain = document.querySelector('#main-cart-items');
    if (miniCartMain) {
      miniCartMain.scrollTop -= miniCartMain.scrollHeight;
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
